/**
 * Cartao "Régua": o plano completo do lead desenhado a partir do marco zero
 * (o que ja foi feito, o proximo, o que esta agendado, o que foi pulado e
 * por que), a pausa local, o teto de ligacoes do RF08 e a janela de comissao.
 *
 * O estado mostrado e DERIVADO: o que veio do servidor (`lead.rule`,
 * `lead.jobStatus`, linha do tempo) combinado com o que a pessoa fez nesta
 * previa (pausou, removeu, marcou venda). Quem decide o texto e
 * `situacaoDaRegua`, construida sobre `situacaoDoLead` (resumo.ts) -- a
 * mesma funcao da tabela e do hero -- para o cartao, o hero e as acoes nunca
 * discordarem sobre "a regua ainda esta viva?".
 *
 * O plano deixa explicito que nenhum degrau fica antes de T0: cada um e
 * "fato + atraso". Nao existe lembrete pre-vencimento nem cobranca
 * preventiva; a comissao existe porque recuperamos quem ja tinha saido.
 */

import { Badge, Footnote, Icon, MetricLine, ProgressBar, SectionCard, Toggle, type BadgeTone } from '../../../components/ui';
import { formatDateTime } from '../../../lib/format';
import { janelaDeCredito, planoDaRegua, situacaoDoLead, type DegrauDoPlano, type EstadoDoDegrau, type EstadoLocalDoLead } from '../resumo';
import type { LeadDetail } from '../types';
import { CANAL, descreverRelativo, duracaoCurta, FATO, SITUACAO } from '../vocabulario';

export type EstadoLocalDaRegua = EstadoLocalDoLead;

export interface SituacaoDaRegua {
  readonly label: string;
  readonly tone: BadgeTone;
  readonly detail: string;
  /** A regua ainda pode disparar algo (pausar/remover fazem sentido). */
  readonly viva: boolean;
}

export function situacaoDaRegua(lead: LeadDetail, local: EstadoLocalDaRegua): SituacaoDaRegua {
  if (local.removida) {
    return { label: 'Removido da régua', tone: 'warning', detail: 'Feito nesta prévia. Nenhum toque será enviado para este lead.', viva: false };
  }
  if (local.vendaMarcada) {
    const semComissao = local.vendaDesfecho === 'sem_contato';
    // Revertida no mesmo ato (estorno da referencia chegou antes): dizer "a comissao fica com o toque" seria cobrar o que a politica desfez.
    const revertida = local.vendaDesfecho === 'creditado_revertido';
    return {
      label: semComissao ? 'Encerrada — venda sem comissão' : revertida ? 'Encerrada — comissão revertida' : 'Encerrada — venda assistida',
      tone: semComissao ? 'neutral' : revertida ? 'warning' : 'good',
      detail: semComissao
        ? 'Registrada nesta prévia. A régua para quando a venda é marcada; sem toque nos 7 dias antes do pagamento, não há comissão.'
        : revertida
          ? 'Registrada nesta prévia. A régua para quando a venda é marcada; já havia estorno desta referência e a política da empresa o reverte, então a comissão foi revertida.'
          : 'Registrada nesta prévia. A régua para quando a venda é marcada; a comissão fica com o último toque da régua.',
      viva: false,
    };
  }
  const info = SITUACAO[situacaoDoLead(lead, local)];
  if (info.viva && local.pausada) {
    return { label: 'Pausada nesta prévia', tone: 'warning', detail: 'O próximo toque fica segurado até você religar a régua.', viva: true };
  }
  return { label: info.viva ? info.label : `Encerrada — ${info.label.toLowerCase()}`, tone: info.tone, detail: info.descricao, viva: info.viva };
}

const ESTILO_DO_DEGRAU: Readonly<Record<EstadoDoDegrau, { readonly circulo: string; readonly texto: string; readonly rotulo: string; readonly icone: 'check' | 'raio' | 'relogio' | 'pausa' | 'x' }>> = {
  feito: { circulo: 'border-status-good/40 bg-status-good/10 text-status-good', texto: 'text-ink', rotulo: 'feito', icone: 'check' },
  proximo: { circulo: 'border-accent bg-accent text-white', texto: 'text-ink', rotulo: 'próximo', icone: 'raio' },
  agendado: { circulo: 'border-line bg-surface text-ink-muted', texto: 'text-ink-muted', rotulo: 'agendado', icone: 'relogio' },
  segurado: { circulo: 'border-status-warning/50 bg-status-warning/10 text-status-warning', texto: 'text-ink-muted', rotulo: 'segurado', icone: 'pausa' },
  pulado: { circulo: 'border-line bg-surface text-ink-subtle', texto: 'text-ink-subtle', rotulo: 'não enviado', icone: 'x' },
};

function DegrauDaRegua({ item, agora }: { readonly item: DegrauDoPlano; readonly agora: Date }): JSX.Element {
  const estilo = ESTILO_DO_DEGRAU[item.status];
  const canal = CANAL[item.degrau.channel];
  const quando =
    item.status === 'feito'
      ? `${descreverRelativo(item.at, agora)} · ${formatDateTime(item.at)}`
      : item.status === 'proximo' || item.status === 'agendado' || item.status === 'segurado'
        ? `${descreverRelativo(item.at, agora)} · ${formatDateTime(item.at)}`
        : `seria ${formatDateTime(item.at)}`;
  return (
    <li className={`group relative flex gap-3 ${item.status === 'proximo' ? '-mx-2 rounded-xl bg-accent/5 px-2 py-1' : ''}`}>
      <div className="flex flex-col items-center">
        <span aria-hidden="true" className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border text-[11px] font-semibold ${estilo.circulo}`}>
          <Icon name={estilo.icone} size="xs" />
        </span>
        <span aria-hidden="true" className="mt-1 w-px flex-1 bg-line group-last:hidden" />
      </div>
      <div className="min-w-0 flex-1 pb-3 group-last:pb-0">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="rounded-md border border-line bg-page px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-ink">{item.degrau.label}</span>
          <span className={`inline-flex items-center gap-1 ${estilo.texto}`}>
            <Icon name={canal.icon} size="xs" />
            {canal.label}
          </span>
          <span className="sr-only">, {estilo.rotulo}</span>
          {item.degrau.isLast ? <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">último</span> : null}
          {item.extra ? (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle" title="Aconteceu fora dos degraus da régua: retentativa atrasada ou link pedido na ligação">
              fora da régua
            </span>
          ) : null}
        </p>
        <p className={`mt-0.5 text-xs ${item.status === 'proximo' ? 'text-accent' : 'text-ink-subtle'}`}>
          <span className="font-medium">{estilo.rotulo}</span> · <span className="tabular-nums">{quando}</span>
          {item.nota === null ? '' : ` · ${item.nota}`}
        </p>
      </div>
    </li>
  );
}

export function CartaoRegua({
  lead,
  local,
  agora,
  onPausar,
}: {
  readonly lead: LeadDetail;
  readonly local: EstadoLocalDaRegua;
  readonly agora: Date;
  readonly onPausar: (pausada: boolean) => void;
}): JSX.Element {
  const situacao = situacaoDaRegua(lead, local);
  const plano = planoDaRegua(lead, local);
  const proximo = situacao.viva && !local.pausada ? lead.rule.nextTouch : null;
  const usadas = lead.rule.voiceAttemptsUsed;
  const teto = lead.rule.voiceAttemptsMax;
  const pausaDiverge = local.pausada !== lead.rule.paused;
  const fato = FATO[lead.funnel];
  const janela = janelaDeCredito(lead, agora, local);
  const temVoz = lead.funnel === 'carrinho_abandonado';

  return (
    <SectionCard
      id="regua-do-lead"
      title="Régua"
      icon="raio"
      aside={
        <Badge tone={situacao.tone} dot>
          {situacao.label}
        </Badge>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-muted">{situacao.detail}</p>

        <div className="flex items-start gap-2 rounded-xl border border-accent/40 bg-accent/5 px-3 py-2 text-xs text-ink-muted">
          <Icon name="escudo" size="xs" className="mt-0.5 shrink-0 text-accent" />
          <span>
            <strong className="text-ink">Marco zero:</strong> {fato.verbo.toLowerCase()} em <span className="tabular-nums">{formatDateTime(lead.abandonedAt)}</span>. Cada toque abaixo é “fato + atraso”; nenhum existe antes dele. A régua só age sobre quem já saiu — é isso que gera a comissão.
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Plano deste lead</h3>
          {plano.length === 0 ? (
            proximo === null ? (
              <p className="text-sm text-ink-muted">{local.pausada && situacao.viva ? 'Próximo toque segurado pela pausa.' : 'Nenhum toque agendado.'}</p>
            ) : (
              <MetricLine
                icon={CANAL[proximo.touch.channel].icon}
                value={proximo.touch.label}
                label={`${CANAL[proximo.touch.channel].label} · ${formatDateTime(proximo.scheduledAt)}`}
                detail={descreverRelativo(proximo.scheduledAt, agora)}
                tone={CANAL[proximo.touch.channel].tone}
              />
            )
          ) : (
            <ol aria-label="Toques da régua, do primeiro ao último" className="flex flex-col">
              {plano.map((item) => (
                <DegrauDaRegua key={item.degrau.touchId} item={item} agora={agora} />
              ))}
            </ol>
          )}
        </div>

        {janela !== null ? (
          <div className="flex flex-col gap-2 border-t border-line pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Janela de comissão</h3>
            <MetricLine
              icon="escudo"
              value={janela.aberta ? `até ${formatDateTime(janela.terminaEm)}` : 'vencida'}
              label={janela.aberta ? `${duracaoCurta((new Date(janela.terminaEm).getTime() - agora.getTime()) / 60_000)} restantes` : `terminou em ${formatDateTime(janela.terminaEm)}`}
              detail={`7 dias após o último contato (${CANAL[janela.canal].label}${janela.touchLabel === '' ? '' : ` ${janela.touchLabel}`})`}
              tone={janela.aberta ? 'good' : 'warning'}
            />
            <Footnote>Uma venda dentro da janela é creditada ao último contato e gera comissão (ADR-001). Fora dela, é venda normal da empresa.</Footnote>
          </div>
        ) : null}

        <div className="flex flex-col gap-2 border-t border-line pt-4">
          <Toggle
            checked={local.pausada}
            onChange={onPausar}
            disabled={!situacao.viva}
            label={
              <span className="inline-flex flex-wrap items-center gap-2">
                Pausar régua para este lead
                {pausaDiverge ? <Badge tone="accent">Salvo na prévia</Badge> : null}
              </span>
            }
            description={situacao.viva ? 'Segura o próximo toque até você religar. Nesta prévia a mudança fica só nesta tela.' : 'A régua já terminou para este lead; não há o que pausar.'}
          />
        </div>

        {temVoz ? (
          <div className="flex flex-col gap-2 border-t border-line pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Ligações da IA</h3>
            <MetricLine
              icon="telefone"
              value={`${usadas} de ${teto}`}
              label={usadas === 1 ? 'ligação usada' : 'ligações usadas'}
              detail="teto de 2 por lead (RF08)"
              tone={usadas >= teto ? 'warning' : 'accent'}
              muted={usadas === 0}
            />
            <ProgressBar value={usadas} max={teto} tone={usadas >= teto ? 'warning' : 'accent'} label="Ligações da IA usadas em relação ao teto" />
            <Footnote>Caixa postal encerra o laço de voz na hora; a régua avança para o WhatsApp sem gastar a segunda ligação.</Footnote>
          </div>
        ) : (
          <Footnote>Expirados não têm toque de voz: a régua é só WhatsApp, em marcos de dias depois do vencimento.</Footnote>
        )}
      </div>
    </SectionCard>
  );
}
